import { SidebarToggleButton } from "@/components/layout/SidebarToggleButton.tsx";
import { cn } from "@/lib/utils.ts";
import {
  readRememberedThreadListOpen,
  readStoredMobileNavTab,
  rememberThreadListOpen,
  resolveInitialThreadListOpen,
  storeMobileNavTab,
} from "@/components/layout/mobileNavTab.ts";
import { CanvasPanel } from "@/components/canvas/CanvasPanel.tsx";
import { parseCalendarParams } from "@/components/calendars/calendar-url.ts";
import { applyCalendarFocus } from "@/components/calendars/calendarFocus.ts";
import { CalendarFocusNavBoundary } from "@/components/calendars/CalendarFocusNavBoundary.tsx";
import { CalendarPageProvider } from "@/views/CalendarPageContext.tsx";
import { Omnibox } from "@/components/messages/Omnibox/Omnibox.tsx";
import { useEventDeepLink } from "@/components/calendars/useEventDeepLink.ts";
import { useImplicitCalendarContext } from "@/components/calendars/useImplicitCalendarContext.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { useCustomMediaQuery } from "@/hooks/useMediaQuery.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { ViewWrapper } from "@/components/layout/ViewWrapper.tsx";
import {
  CANVAS_COLUMN_DEFAULT_WIDTH,
  CANVAS_COLUMN_MIN_WIDTH,
  CANVAS_COLUMN_REACH,
  CANVAS_COLUMN_SPLIT_STORAGE_KEY,
  CanvasSplitPanelLayout,
  PAGE_COLUMN_MIN_WIDTH,
  THREE_COLUMN_MIN_VIEWPORT,
} from "@/components/layout/SplitPanel/CanvasSplitPanelLayout.tsx";
import { useCanvasCalendarNav } from "@/components/canvas/useCanvasCalendarNav.ts";
import { useCanvasCalendarSurface } from "@/components/canvas/useCanvasCalendarSurface.tsx";
import { CanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { CanvasNavBoundary } from "@/components/canvas/CanvasNavBoundary.tsx";
import { ThreadNavBoundary } from "@/components/navigation/ThreadNavBoundary.tsx";
import {
  type ShellUrlPatch,
  type ShellUrlWriteOptions,
  useShellUrlWriter,
} from "@/components/navigation/shellUrl.ts";
import { useEphemeralCanvas } from "@/components/canvas/useEphemeralCanvas.ts";
import { useEmbedded } from "@/hooks/useEmbedded.tsx";
import { addDays, addMonths } from "date-fns";
import {
  isItemPivot,
  PIVOT_LABELS,
  PIVOT_PATHS,
  pivotForPath,
  pivotItemForPath,
  pivotItemPath,
  type ItemPivot,
  type Pivot,
} from "@/components/layout/pivotRoutes.ts";
import { CanvasLayers } from "@/components/canvas/CanvasLayers.tsx";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { EventDetailBody } from "@/components/calendars/EventDetailBody.tsx";
import { ThreadCanvasView } from "@/views/Thread.tsx";
import { useCanvasColumn } from "@/components/canvas/useCanvasColumn.ts";
import { useThreadCanvas } from "@/components/threads/useThreadCanvas.tsx";
import { SplitPanelFrame } from "@/components/layout/SplitPanel/SplitPanelFrame.tsx";
import { SplitPanelLayout } from "@/components/layout/SplitPanel/SplitPanelLayout.tsx";
import { ConnectorsCanvasView } from "@/views/ConnectorsGallery.tsx";
import { LibraryCanvasView } from "@/views/Library.tsx";
import { NewChatCanvasView } from "@/components/agent-triggers/CreateOrAskDialog.tsx";
import { ContactsCanvasView } from "@/views/Contacts.tsx";
import { RoutinesCanvasView } from "@/views/Routines.tsx";
import { SkillsCanvasView } from "@/views/Skills.tsx";
import { TasksPivotView } from "@/components/userTasks/TasksPivotView.tsx";
import { ThreadListPanel } from "@/components/threads/thread-list/ThreadListPanel.tsx";
import { useTriggerThreadFlash } from "@/components/threads/threadFlash.ts";
import { getThreadIdFromPath, getThreadPagePath } from "@/components/threads/util.ts";
import { confirmationToast } from "@/components/ui/toast.tsx";
import { MobileNavMenu, type MobileDestination } from "@/components/layout/MobileNavMenu.tsx";
import { useShortcut } from "@/components/keyboard/useShortcut.ts";
import { newChatSignalAtom, openThreadsPanelSignalAtom } from "@/components/keyboard/navSignals.ts";
import { useAtomValue, useSetAtom } from "jotai";
import { AccountSwitcher } from "@/components/accountSwitcher/AccountSwitcher.tsx";
import { SidebarFooter } from "@/components/layout/SidebarFooter.tsx";
import { RightSidebarHost } from "@/components/right-sidebar/RightSidebar.tsx";
import { Nux } from "./Nux/Nux.tsx";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useSearch } from "wouter";

/**
 * What the canvas column is showing: one of the sidebar's views, or a thread
 * opened from the list below them. One value rather than a pivot plus an open
 * thread id, so the two can never both claim the column — and so "no pivot is
 * current" is something the state says rather than something the page has to
 * remember to derive.
 */
type CanvasView =
  // `itemId` is the one of the destination's own things it has singled out — a
  // contact, a routine, a skill, the event whose popover is open on the
  // calendar. Still the same destination: the sidebar row stays current and a
  // card opened over it stays up, because going into a contact is not leaving
  // Contacts and selecting an event is not leaving the calendar.
  | { kind: "pivot"; pivot: Pivot; itemId?: string }
  // `from` is the list the thread was drilled into from, when it was one — the
  // Library row, the task's thread. Absent when it was picked from the sidebar,
  // which is not a parent to return to but the list that is always there.
  | { kind: "thread"; threadId: number; from?: Pivot };

/**
 * What a fresh session opens on. New chat rather than the calendar: home's job
 * is to take a request, and the calendar is one of the things you can ask about
 * rather than the thing you came to do.
 */
const DEFAULT_PIVOT: Pivot = "newchat";

/**
 * Read the canvas view out of the URL, so a reload lands where the user was.
 *
 * The path names the destination and `?thread=` overlays a thread on it, which
 * is why the thread is read first: while one is open no destination is current,
 * and the path underneath is only the list it can be closed back to. A path
 * that names no destination is home — the app root mounts this page on the
 * pivot paths and on `/`, and `/` is New chat.
 *
 * `from` is recognised by membership in `PIVOT_LABELS`, which is a
 * `Record<Pivot, …>`: adding a destination to the union without a label is a
 * compile error there, so the URL cannot fall behind the sidebar.
 */
function parseCanvasView(path: string, params: URLSearchParams): CanvasView {
  const threadId = getThreadIdFromPath(path);
  if (threadId !== undefined) {
    const from = params.get("from");
    return {
      kind: "thread",
      threadId,
      from: from && from in PIVOT_LABELS ? (from as Pivot) : undefined,
    };
  }
  // Before the bare destination, since an item's path is its list's path with a
  // segment on the end and `pivotForPath` answers null for it.
  const item = pivotItemForPath(path);
  if (item) return { kind: "pivot", pivot: item.pivot, itemId: item.itemId };
  return { kind: "pivot", pivot: pivotForPath(path) ?? DEFAULT_PIVOT };
}

/**
 * What moving to a canvas view changes about the URL: the destination's own
 * path — for a thread its own page, `/threads/:id`, the URL every other surface
 * in the app already links a thread by — and the one param this page owns.
 *
 * Every other param rides along untouched: the calendar's `?date=`/`?view=`,
 * the sidebar's `sb_` keys. That is the URL writer's guarantee, not this
 * function's job; it names only what it means to change.
 */
function canvasViewPatch(view: CanvasView): ShellUrlPatch {
  if (view.kind === "pivot") {
    // An open item names itself in the path. `isItemPivot` rather than a cast:
    // only some destinations have items, and the view type cannot say which
    // without splitting a variant off that every reader would then have to
    // handle to learn something they mostly do not care about.
    const path =
      view.itemId && isItemPivot(view.pivot)
        ? pivotItemPath(view.pivot, view.itemId)
        : PIVOT_PATHS[view.pivot];
    return { path, params: { from: null } };
  }
  // The list a thread was drilled into from, which its header names as the way
  // back. A query param rather than a path segment: it says where the user came
  // from, not what they are looking at, and a thread reached any other way — a
  // link, a notification, a reload — is the same page without it.
  return { path: getThreadPagePath(view.threadId), params: { from: view.from ?? null } };
}

export const Calendar = () => {
  const { getUserTimezone } = useCurrentUserContext();
  const [location] = useLocation();
  // Seed state from URL query params (`?date=&view=&r=`). Read once on mount;
  // subsequent user interaction (drag, prev/next, view switch) updates local
  // state only — we don't rewrite the URL. If the user reloads with stale
  // params, we re-seed from them on the next mount. Shared links land on the
  // right date/view/ranges; a plain `/calendar` opens to defaults.
  const searchString = useSearch();
  // biome-ignore lint/correctness/useExhaustiveDependencies: seed once on mount
  const urlSeed = useMemo(() => parseCalendarParams(new URLSearchParams(searchString)), []);

  // Deep-link entry for daily-brief suggestion emails: `/?input=<prompt>` opens
  // the Create-or-ask Omnibox prefilled with the prompt (the calendar is the
  // app's home route now, so these links land here rather than the legacy Home).
  // Read once on mount; the param is stripped below so refresh/back don't re-open.
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once on mount
  const autoOpenInput = useMemo(() => new URLSearchParams(searchString).get("input"), []);

  // View + date come from the shared canvas nav store, so navigating home ⇄ thread
  // keeps the same view/range and the calendar doesn't appear to reflow. The view
  // is a durable preference (survives reloads and app closes); the date carries an
  // idle max-age — within the window an in-tab reload rehydrates it, and once it
  // lapses (or on a cold start) the calendar opens on today. A URL `?view=`/`?date=`
  // on a shared link still wins on mount via the seed effect below. `setViewType`
  // snaps to today on a user-driven switch (header/keyboard).
  const { currentDate, setCurrentDate, viewType, setViewType, goToToday } = useCanvasCalendarNav();

  // Override the stored view/date/selection with the URL's on mount so a shared
  // link lands on the right day, view, and highlighted ranges. Same applier the
  // focus boundary uses, so a deep link and an in-app link produce identical
  // state. The store hydrates synchronously, so a useLayoutEffect keeps the
  // initial paint synced without a flash of the previously-stored state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: apply once on mount
  useLayoutEffect(() => {
    applyCalendarFocus(urlSeed, getUserTimezone());
  }, []);

  const isMobile = useIsMobile();

  // Whether the left sidebar (the thread list) is showing; false collapses it to
  // zero width. Desktop is session-only: it starts open and stays as the user
  // left it while they're on the page, but a refresh re-opens it. Mobile is
  // sticky: the Calendar | Chats choice persists across reloads and app closes
  // (see mobileNavTab.ts), defaulting to Chats for a fresh session.
  //
  // Seed from the viewport read *synchronously*, not from `useIsMobile()`: that
  // atom is `false` on the first render (its effect updates post-mount), and the
  // initializer runs only once — so reading it here would always take the
  // desktop branch. `MOBILE_BREAKPOINT` mirrors use-mobile.tsx.
  const [isThreadListOpen, setThreadListOpen] = useState<boolean>(() =>
    resolveInitialThreadListOpen({
      remembered: readRememberedThreadListOpen(),
      isMobileWidth: typeof window !== "undefined" && window.innerWidth < 768,
      storedMobileTab: readStoredMobileNavTab(),
    }),
  );
  // Mirror the current state into module memory so a remount (after navigating
  // away and back) can restore it.
  useEffect(() => {
    rememberThreadListOpen(isThreadListOpen);
  }, [isThreadListOpen]);
  // Mobile keeps the single-column calendar with no canvas, matching the thread
  // page, so consumers there fall back to a real page.
  const showCanvas = !isMobile;
  const { isWebView } = useEmbedded();

  const writeShellUrl = useShellUrlWriter();

  // ⌘B toggles the sidebar open/closed; `g t` (its effect below) only ever
  // opens it. Functional setState keeps the callback's identity stable so the
  // ⌘B shortcut isn't re-registered on every toggle.
  const handleToggleThreadList = useCallback(() => {
    setThreadListOpen((prev) => !prev);
  }, []);
  // What the canvas is showing, read straight off the URL rather than mirrored
  // into state beside it. The nav list and the thread list navigate; this
  // re-derives. One source of truth means a reload, the browser's Back gesture,
  // and a pasted link all arrive the same way an in-app click does, and there is
  // no window where the address bar and the column disagree.
  const canvasView = useMemo(
    () => parseCanvasView(location, new URLSearchParams(searchString)),
    [location, searchString],
  );
  // Navigating IS setting the view. Pushed rather than replaced: a destination
  // is a place you went, so Back should return to the last one — which is the
  // point of giving each its own address. Through the URL writer, which is
  // stable, so the handlers built on this stay stable across a navigation —
  // which `TaskList` and the thread list, both memoized, depend on.
  const setCanvasView = useCallback(
    (view: CanvasView, options?: ShellUrlWriteOptions) =>
      writeShellUrl(canvasViewPatch(view), options),
    [writeShellUrl],
  );
  // Null while a thread is showing — which is what the nav list wants, so no
  // view reads as current while the canvas is on something else.
  const activePivot = canvasView.kind === "pivot" ? canvasView.pivot : null;
  // Which canvas the column beside the page draws.
  //
  // A thread brings its own: it is a property of the thread — persisted,
  // restored on re-entry, written by the server when the agent presents an
  // artifact mid-turn — and a conversation running on this page's would lose
  // the pairing every artifact surface depends on. Everything else shares this
  // page's, whose content is deliberately ephemeral: an event opened from the
  // calendar does not survive navigating away.
  //
  // Both are called every render because hooks must be; the thread's query is
  // gated on there being a thread, so the resting cost is nothing.
  const canvasThreadId = canvasView.kind === "thread" ? canvasView.threadId : null;
  const ephemeralCanvas = useEphemeralCanvas();
  const threadCanvas = useThreadCanvas(canvasThreadId ?? Number.NaN, {
    enabled: showCanvas && canvasThreadId !== null,
  });
  const activeCanvas = canvasThreadId !== null ? threadCanvas : ephemeralCanvas;
  // The column's own open/closed state and card size, whichever canvas is behind
  // it. It rests closed, reveals itself when something is presented, and fills
  // itself with what it reveals; see the hook for why neither canvas is allowed
  // to answer those questions itself.
  const {
    isOpen: isCanvasOpen,
    isFullscreen,
    close: closeCanvas,
    actions: canvasActions,
  } = useCanvasColumn(activeCanvas.canvasActions);
  const canvasStack = activeCanvas.canvasStack;

  // Three columns need the thread list, the page's own floor and the canvas
  // column side by side. Below that width something has to give, and left alone
  // it is the page: a resting width is CSS, while the split's `minLeft` and
  // `minRight` only clamp drags, so the page column takes whatever subtraction
  // leaves it — 40px on an 800px window. Collapse the list instead. It is the
  // column whose absence costs least here, and its toggle moves into the page
  // header as it goes, so the way back stays on screen.
  //
  // Not restored when the window widens again: this writes the user's own
  // open/closed state, and re-opening a column they can see is collapsed would
  // be the window resizing their layout back at them.
  const fitsThreeColumns = useCustomMediaQuery(`(min-width: ${THREE_COLUMN_MIN_VIEWPORT}px)`);
  useEffect(() => {
    if (isCanvasOpen && !fitsThreeColumns) setThreadListOpen(false);
  }, [isCanvasOpen, fitsThreeColumns]);

  // The event the calendar has singled out, which is what `/calendar/event/:id`
  // says. Null whenever the canvas is anywhere else, so a selection cannot
  // outlive the screen it was made on.
  const selectedEventId =
    canvasView.kind === "pivot" && canvasView.pivot === "calendar"
      ? (canvasView.itemId ?? null)
      : null;
  // Select an event, or clear the selection with null. Selecting is a move to
  // the event's own address rather than a popover the grid opens behind the
  // URL's back, so a selection can be linked to, survives a reload, and closes
  // on Back (BIG-7804).
  //
  // Opening from the bare calendar pushes, so Back closes the selection rather
  // than leaving the calendar. Every write while one is already open replaces —
  // moving chip to chip, and closing. Clicking through a dozen events is
  // browsing, not a dozen places you went, and pushing each would bury the way
  // out twelve presses deep.
  const selectEvent = useCallback(
    (eventId: string | null) =>
      setCanvasView(
        { kind: "pivot", pivot: "calendar", itemId: eventId ?? undefined },
        { replace: selectedEventId !== null },
      ),
    [setCanvasView, selectedEventId],
  );
  const clearSelectedEvent = useCallback(() => selectEvent(null), [selectEvent]);
  // `/calendar?eventId=…` — a calendar reminder's notification tap, which the
  // surface turns into the event's card once it resolves.
  const eventDeepLink = useEventDeepLink();
  const canvasSurface = useCanvasCalendarSurface({
    // Mobile has no canvas to card into and no room to anchor a popover, so the
    // event fills the column — but it is the same selection at the same
    // address, so it goes through the same setter rather than a navigation of
    // its own.
    onMobileEventClick: selectEvent,
    selectedEventId,
    onSelectEvent: selectEvent,
    deepLink: eventDeepLink,
    // Handed in rather than read from context: this component renders the
    // provider below, and React resolves a context from above the reader.
    canvas: showCanvas ? canvasActions : undefined,
  });
  const { selection } = canvasSurface;
  // Mobile shows one panel at a time, and which one is the pivot the URL names
  // — the same view desktop reads, rendered in the one column mobile has.
  //
  // A thread resolves to Chats rather than to a destination of its own, because
  // Chats is where it was picked from: the tab says which part of the app you
  // are in, and the conversation is still that part. What the column shows is
  // decided below.
  //
  // New chat is a destination here, as it is on desktop: a screen you go to,
  // with the menu on its bottom edge like every other.
  //
  // Which leaves Chats needing an answer of its own. `isThreadListOpen` is it:
  // the same flag desktop uses for its sidebar, whose resolver already describes
  // itself as "Chats on mobile, the sidebar on desktop". The list has no path of
  // its own to read instead — it is the one destination the URL does not name.
  //
  // Only at the address the list shares with New chat. The flag says the list is
  // the tab you left off on, not that it outranks a destination the URL names:
  // without the pivot check it covered `/routines` and every other path too, so
  // a link into one of them landed on the list instead.
  const mobileShowsThreadList =
    isThreadListOpen && canvasView.kind === "pivot" && canvasView.pivot === DEFAULT_PIVOT;
  const mobileDestination: MobileDestination =
    canvasView.kind === "thread" || mobileShowsThreadList ? "threads" : canvasView.pivot;
  // A presented card belongs to the view it was opened over, so leaving that
  // view takes it along. Without this a People card opened from a thread row
  // stayed up over Routines, over the calendar, over whatever came next —
  // chrome from a screen the user had already left, with its own close button as
  // the only way out.
  //
  // Keyed on where the canvas is rather than on the object holding it: the view
  // is re-derived whenever the URL changes, so comparing identity would clear
  // the card on writes that did not move anywhere.
  const canvasDestinationKey =
    canvasView.kind === "thread" ? `thread:${canvasView.threadId}` : `pivot:${canvasView.pivot}`;
  // This page's own canvas, never the active one. A thread's is persisted
  // server-side and restored on re-entry, so clearing it on the way out would
  // throw away the artifact the agent presented — leaving the thread is not the
  // user dismissing it.
  const clearCanvasCards = ephemeralCanvas.canvasActions.clear;
  // The previous destination is read in the body on purpose. The effect exists
  // to run when the key changes and does not otherwise need its value, and
  // exhaustive-deps strips a dependency the body never mentions — which quietly
  // turns this into a run-once-on-mount effect. Comparing against a ref both
  // keeps the dependency honest and skips the mount pass, where there is
  // nothing open to close.
  const lastDestination = useRef(canvasDestinationKey);
  useEffect(() => {
    if (lastDestination.current === canvasDestinationKey) return;
    lastDestination.current = canvasDestinationKey;
    clearCanvasCards();
    // Shut behind the destination that opened it. The column is somewhere
    // things are put rather than somewhere you are, so arriving anywhere new
    // starts from closed — including on a thread, whose own canvas may well hold
    // an artifact from last time and should wait to be asked for.
    closeCanvas();
    // The prefill belongs to the composer it was seeded for, or a later manual
    // New chat opens mid-sentence in someone else's task. On leaving only: the
    // seed is set in the same gesture that selects this pivot, so clearing on
    // arrival would wipe it before the composer could read it.
    if (canvasDestinationKey !== "pivot:newchat") setSeededInput("");
  }, [canvasDestinationKey, clearCanvasCards, closeCanvas]);

  const openThreadId = canvasView.kind === "thread" ? canvasView.threadId : null;
  // The destination a thread is showing over, which is simply the path it was
  // opened on: a thread adds `?thread=` and leaves the path alone, so what the
  // canvas was showing before is still written down in the URL. That also
  // survives a cold load, which a remembered value cannot — landing straight on
  // `/tasks?thread=5` has no "before" to have remembered.
  const pivotBehindThread = pivotForPath(location) ?? DEFAULT_PIVOT;
  // Prefill for the `?input=` deep link, cleared once the composer is left so a
  // later manual open starts blank.
  const [seededInput, setSeededInput] = useState("");
  const newChatSignal = useAtomValue(newChatSignalAtom);
  const requestNewChat = useSetAtom(newChatSignalAtom);
  const selectPivot = useCallback(
    (pivot: Pivot) => setCanvasView({ kind: "pivot", pivot }),
    [setCanvasView],
  );
  // Drilling a destination's list into one of its items, and — with null — the
  // way back out to the list. Pushed like any other move, so Back returns to the
  // list and a link to the item opens it directly.
  //
  // Curried per destination so each list hands its own opener down and cannot
  // name someone else's; the three below are stable, which the memoized lists
  // they are handed to depend on.
  const openPivotItem = useCallback(
    (pivot: ItemPivot) => (itemId: string | null) =>
      setCanvasView(
        { kind: "pivot", pivot, itemId: itemId ?? undefined },
        // Opening pushes, so Back closes the item. Leaving it replaces: the
        // list is where the user already was, and pushing it on top would put
        // the item back on the next Back. That matters most where the way out
        // is a deletion — Back would return to a contact that no longer exists.
        { replace: itemId === null },
      ),
    [setCanvasView],
  );
  const openContact = useMemo(() => openPivotItem("contacts"), [openPivotItem]);
  const openRoutine = useMemo(() => openPivotItem("routines"), [openPivotItem]);
  const openSkill = useMemo(() => openPivotItem("skills"), [openPivotItem]);
  // Persist only the two the mobile switcher used to offer, which are the two
  // this page restores on a return visit. The rest live in the URL, which is
  // where a destination the user navigated to belongs.
  const handleSelectMobileDestination = useCallback(
    (destination: MobileDestination) => {
      if (destination === "threads" || destination === "calendar") {
        storeMobileNavTab(destination === "threads" ? "chats" : "calendar");
      }
      // Chats is the one row that is not a pivot, so it is this flag that shows
      // it and every other row that has to put it away.
      setThreadListOpen(destination === "threads");
      // New chat is the one row the menu answers twice: it raises the new-chat
      // signal as well as calling here, and the effect that listens for that
      // signal does the move. Doing it here too pushed the same address onto
      // history a second time, so leaving took two Backs that changed nothing.
      if (destination === "newchat") return;
      selectPivot(destination === "threads" ? DEFAULT_PIVOT : destination);
    },
    [selectPivot],
  );

  // Opening a thread, wherever the request came from — the list, the toast, a
  // composer that just created one. One path at either width: the thread's own
  // page, shown by this shell in the column the destinations share. Desktop
  // keeps the list beside it; mobile, having one column, gives that column to
  // the conversation.
  const openThread = useCallback(
    (threadId: number, from?: Pivot) => setCanvasView({ kind: "thread", threadId, from }),
    [setCanvasView],
  );
  // Opening from a list *on* the canvas, which the header then names as the way
  // back. One stable callback each rather than a curried factory: `TaskList` is
  // memoized, and a closure minted per render would hand it a new prop every
  // time and defeat that bailout.
  const openThreadFromTasks = useCallback(
    (threadId: number) => openThread(threadId, "tasks"),
    [openThread],
  );
  const openThreadFromLibrary = useCallback(
    (threadId: number) => openThread(threadId, "library"),
    [openThread],
  );
  // Published on the page context for the agent-trigger popovers: a thread
  // started from an event chip belongs to the calendar it was fired from.
  const openThreadFromCalendar = useCallback(
    (threadId: number) => openThread(threadId, "calendar"),
    [openThread],
  );
  // Where a thread's back button, breadcrumb, and Escape go. The list it was
  // drilled into from wins over the ref: on a cold load — a reloaded
  // `?thread=…&from=library` — nothing was showing before it, so the ref still
  // holds the default and the header would name Library while sending the user
  // to New chat.
  const threadOrigin = canvasView.kind === "thread" ? canvasView.from : undefined;
  // `options` carries the one thing the conversation knows and this doesn't:
  // whether it is still there to come back to. A destination is normally pushed,
  // because leaving a thread is a move Back should undo — but a deleted thread's
  // address answers 403, so that exit asks to replace this entry rather than
  // leave it sitting behind the new one.
  const leaveThreadCanvas = useCallback(
    (options?: ShellUrlWriteOptions) =>
      setCanvasView({ kind: "pivot", pivot: threadOrigin ?? pivotBehindThread }, options),
    [threadOrigin, pivotBehindThread, setCanvasView],
  );

  // The one place a new-chat request turns into something. Skips the first
  // render's value so a fresh mount does not read the standing signal as a
  // request — the counter is module-global and outlives this screen.
  const firstNewChatSignal = useRef(newChatSignal);
  useEffect(() => {
    if (newChatSignal === firstNewChatSignal.current) return;
    // Record what was handled, or the guard only holds until the first request:
    // after that the comparison stays true and the body re-runs whenever another
    // dependency changes. `isMobile` is one, and it flips whenever a window
    // crosses the breakpoint — which navigated the reader away from wherever
    // they were, and took the sidebar down with it.
    firstNewChatSignal.current = newChatSignal;
    // Mobile only. The same flag is desktop's sidebar, which has no reason to
    // collapse because the canvas started a new chat.
    if (isMobile) setThreadListOpen(false);
    selectPivot("newchat");
  }, [newChatSignal, isMobile, selectPivot]);

  // ⌘K (always on) plus c / n / / / space (when single-key shortcuts are
  // enabled). It lives here, on the page, because the page is what routes the
  // request — and `useShortcut` binds per call site, so one page means one
  // binding rather than one per trigger.
  //
  // Opens an empty composer. A seed only clears once its thread is created, so
  // a `?input=` deep link the user walked away from would otherwise come back
  // here — and a field that isn't empty is one ↑ won't fill (BIG-8149).
  useShortcut("agent.open", () => {
    setSeededInput("");
    requestNewChat((n) => n + 1);
  });

  // Start a chat with words already in it. The same pair the `?input=` deep
  // link fires below, handed down to the views that offer it: the page owns the
  // seed and where a new chat opens, so a view supplies only the sentence.
  const seedNewChat = useCallback(
    (text: string) => {
      setSeededInput(text);
      requestNewChat((n) => n + 1);
    },
    [requestNewChat],
  );

  // A `?input=` deep link opens the composer with the prompt already in it.
  // Runs once: the page reads the param once and strips it below.
  const deepLinkApplied = useRef(false);
  useEffect(() => {
    if (deepLinkApplied.current || !autoOpenInput) return;
    deepLinkApplied.current = true;
    setSeededInput(autoOpenInput);
    requestNewChat((n) => n + 1);
  }, [autoOpenInput, requestNewChat]);

  // Strip the consumed `?input=` deep-link param on mount so a refresh or back
  // navigation doesn't re-open the prefilled composer. The value was captured
  // once into `autoOpenInput` above, so removing it here is safe.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    if (!autoOpenInput) return;
    writeShellUrl({ params: { input: null } }, { replace: true });
  }, []);

  // "Send in background": confirm the hand-off without stealing focus. The
  // thread is already created and running; we just acknowledge it. If the
  // threads list is open on the left, flash the new row so the user can see
  // where it landed; the toast's click target opens the thread's page.
  const triggerThreadFlash = useTriggerThreadFlash();
  const notifyThreadSentInBackground = useCallback(
    (threadId: number) => {
      confirmationToast("I'm on it", undefined, {
        description: "I'll let you know if I need anything.",
        onToastClick: () => openThread(threadId),
        actionStatsigName: "send-in-background-toast",
      });
      // Only worth flagging a row the reader will see. Desktop shows the list in
      // its sidebar; on a phone it is on screen only while the flag that picks
      // it over New chat at their shared address is set — which composing is
      // not, so a send from that screen flags nothing and the toast carries it.
      if (isMobile ? mobileShowsThreadList : isThreadListOpen) {
        triggerThreadFlash(threadId);
      }
    },
    [openThread, isMobile, mobileShowsThreadList, isThreadListOpen, triggerThreadFlash],
  );
  // What a sent composer does, at either width. `background` (decided by the
  // submit gesture in the Omnibox) keeps the user where they are with a toast
  // and a row flash; otherwise the new thread opens. Either way the composer is
  // left behind and the deep-link prefill is cleared, so a later visit to the
  // New chat screen starts blank.
  const handleThreadCreated = useCallback(
    (threadId: number, background: boolean) => {
      setSeededInput("");
      // Sent in the background: the reader stays where they were, which is the
      // whole meaning of the gesture, and the toast is the way in. The same at
      // either width — a phone that jumped to the list instead would be the one
      // surface where sending in the background moved you.
      //
      // Nothing to navigate to: this composer is the New chat screen's own, so
      // the pivot it would select is the one already showing. Selecting it
      // anyway pushed a second entry for the same address, and the Back that
      // consumed it looked like a press that did nothing.
      if (background) {
        notifyThreadSentInBackground(threadId);
        return;
      }
      openThread(threadId);
    },
    [notifyThreadSentInBackground, openThread],
  );

  // A thread started from the composer standing on a pivot's own canvas.
  // Unlike the New chat surface there is nothing to leave — the user is on
  // Routines or Tasks and stays there — so a background send is only the toast,
  // and an opened thread names the pivot it came from rather than defaulting to
  // whatever the last one was.
  const handleCanvasThreadCreated = useCallback(
    (threadId: number, background: boolean) => {
      if (background) {
        notifyThreadSentInBackground(threadId);
        return;
      }
      openThread(threadId, canvasView.kind === "pivot" ? canvasView.pivot : undefined);
    },
    [notifyThreadSentInBackground, openThread, canvasView],
  );

  // Every pivot except New chat, which has its own composer at the centre of
  // the page and would otherwise have two. A thread on the canvas has one too,
  // in its own footer.
  const canvasComposer = !isMobile &&
    canvasView.kind === "pivot" &&
    canvasView.pivot !== "newchat" && (
      // `bottom-8` is where the New chat pill it replaces stood, which is the
      // strongest band of the shim behind it. The wrapper takes no pointer
      // events so the surface underneath stays draggable either side of the
      // composer.
      <div className="pointer-events-none absolute inset-x-0 bottom-8 z-30 flex justify-center px-4">
        <Omnibox
          className="pointer-events-auto w-full max-w-[688px]"
          newThreadOnly
          onThreadCreated={handleCanvasThreadCreated}
          // The composer is standing where a button used to, on a surface the
          // user came to look at rather than to type on. Taking focus on
          // arrival would swallow the first keystroke on desktop and raise the
          // keyboard on a phone. The New chat surface still takes it, because
          // going there is the act of choosing to write.
          focusType="never"
        />
      </div>
    );

  // A reference to another thread inside a message. Claimed so it opens on this
  // page's canvas like every other way in; declined on mobile, where a thread
  // really is its own page.
  const openThreadFromLink = useCallback(
    (threadId: number) => {
      if (isMobile) return false;
      openThread(threadId);
      return true;
    },
    [isMobile, openThread],
  );

  const calendarPageValue = useMemo(
    () => ({ notifyThreadSentInBackground, openThread: openThreadFromCalendar }),
    [notifyThreadSentInBackground, openThreadFromCalendar],
  );

  // Registers an implicit calendar-context enrichment so any Omnibox the user
  // opens on this page (the New chat composer today; popovers in later phases)
  // sees the visible day/week/month range. Suppressed when manual ranges are
  // selected — the RangeSelectionPopover provides those explicitly.
  useImplicitCalendarContext(viewType, currentDate, selection.ranges.length > 0);

  // --- Calendar keyboard shortcuts (Google-Calendar-style). When the chat
  // panel is showing it owns `j`/`k` (next/prev *thread*), so the calendar's
  // `j`/`k` (next/prev *period*) only fire while it isn't. With the calendars
  // tab open the grid is the primary surface, so period keys stay live.
  // (ThreadList binds its own j/k whenever mounted — including the warm
  // collapsed panel — identical to today's closed-state semantics.) View
  // switches (d/w/m/x, t) stay available either way. ---
  const calendarPeriodKeys = !isThreadListOpen;
  const shiftPeriod = useCallback(
    (dir: 1 | -1) => {
      setCurrentDate((d) => {
        switch (viewType) {
          case "week":
            return addDays(d, 7 * dir);
          case "month":
            return addMonths(d, dir);
          default:
            // Day and multi-day both step a single day — matching CalendarHeader's
            // prev/next buttons (multi-day's 2-day window scrolls one day at a time).
            return addDays(d, dir);
        }
      });
    },
    [viewType, setCurrentDate],
  );
  useShortcut("calendar.viewDay", () => setViewType("day"), {
    deps: [setViewType],
  });
  useShortcut("calendar.viewWeek", () => setViewType("week"), {
    deps: [setViewType],
  });
  useShortcut("calendar.viewMonth", () => setViewType("month"), {
    deps: [setViewType],
  });
  useShortcut("calendar.viewMultiDay", () => setViewType("multi-day"), {
    deps: [setViewType],
  });
  useShortcut("calendar.today", goToToday, { deps: [goToToday] });
  useShortcut("calendar.prev", () => shiftPeriod(-1), {
    enabled: calendarPeriodKeys,
    deps: [shiftPeriod],
  });
  useShortcut("calendar.next", () => shiftPeriod(1), {
    enabled: calendarPeriodKeys,
    deps: [shiftPeriod],
  });
  // ⌘B toggles the chat/threads panel (the calendar's "sidebar").
  useShortcut("system.toggleSidebar", handleToggleThreadList, {
    deps: [handleToggleThreadList],
  });

  // `g t` (handled globally) bumps this counter to open the threads panel here.
  // Track the last-handled value (seeded to the current value on mount) so we
  // only react to a *new* bump — otherwise the never-reset counter would re-open
  // the panel on every Calendar remount after the first `g t`.
  const openThreadsPanelSignal = useAtomValue(openThreadsPanelSignalAtom);
  const lastHandledPanelSignal = useRef(openThreadsPanelSignal);
  useEffect(() => {
    if (openThreadsPanelSignal !== lastHandledPanelSignal.current) {
      lastHandledPanelSignal.current = openThreadsPanelSignal;
      setThreadListOpen(true);
    }
  }, [openThreadsPanelSignal]);

  // Mobile: the composer and the nav menu share one bottom bar at the body level
  // (not the grid area) so it stays put across every destination.
  // Edge insets and control scale mirror the native footer. The create-glow shim
  // (.mobile-create-shim, with pt-32 for its fade room) rides the bar wherever a
  // composer is on it, so those screens read identically.
  //
  // Not over any second-level screen. A conversation is the obvious one — it
  // brings its own composer in its footer, and the bar is positioned over the
  // body, so leaving it up floats a new-chat field on top of the field for the
  // conversation being read. But an open routine, contact or skill is the same
  // kind of screen: drilled into from the list behind it, with a chevron back to
  // that list, and a composer over it offers a new chat on top of the thing just
  // opened. The same reason `canvasComposer` above stands down for a thread; the
  // header's back control is the way out of either.
  //
  // Read off the view rather than reported up from the panel that draws the
  // breadcrumb: an open item names itself in the path now, so the shell can see
  // a drilled-in screen the same way it already sees a thread.
  const canvasDrilledIn =
    canvasView.kind === "pivot" && isItemPivot(canvasView.pivot) && canvasView.itemId !== undefined;
  // New chat carries its own composer at the centre of the screen, so the bar
  // keeps only the menu there rather than floating a second field under the
  // first. The same reason `canvasComposer` stands down on that pivot.
  //
  // The glow goes with it. That gradient exists to lift a composer off the list
  // scrolling under it; with no composer on the band it is a purple wash across
  // the foot of a screen with nothing there to separate, and the menu's own
  // shadow already sets it off the page.
  //
  // Keyed on the screen actually being shown, not on the pivot alone: `/` parses
  // to New chat, and that is also the address the thread list sits at, so the
  // pivot on its own would take the composer off the list as well.
  const showsNewChatScreen =
    !mobileShowsThreadList && canvasView.kind === "pivot" && canvasView.pivot === "newchat";
  const showsBottomComposer = !showsNewChatScreen;
  const mobileBottomBar = isMobile && canvasView.kind !== "thread" && !canvasDrilledIn && (
    <div
      className={cn(
        "group/shim pointer-events-none absolute inset-x-0 bottom-0 z-30",
        showsBottomComposer && "mobile-create-shim pt-32",
      )}
    >
      {/* The controls' own band. Where a composer is on it, the band takes taps
          rather than passing them through: a card read through the shim was
          still answering clicks, so a tap beside the composer opened a thread
          the reader was only trying to type next to. With just the menu there is
          nothing to protect, and a transparent strip across the foot of the
          screen would swallow taps meant for what is behind it — so there the
          menu alone takes them. The glow above this band passes them through
          either way, leaving the list below still scrollable where the shim is
          only a tint.

          `py-6` because both controls carry `--shadow-bbl`, which reaches about
          24px below them (12px of offset plus 16px of blur, less 4px of negative
          spread). At 20px the last of it ran off the bottom of the screen and
          the pill read as sitting on the edge rather than above it. The sides
          are already clear at 16px, where the same shadow reaches about 12px. */}
      <div
        className={cn(
          "flex items-end px-4 py-6",
          showsBottomComposer ? "pointer-events-auto" : "pointer-events-none",
        )}
        // A tap on the band is also how the composer is dismissed: it drops
        // focus, which collapses the row back to its resting width. Guarded on
        // the band itself as the target, because a tap on the composer or the
        // menu bubbles through here on its way out and is theirs, not this.
        onPointerDown={(event) => {
          if (event.target !== event.currentTarget) return;
          const focused = document.activeElement;
          if (focused instanceof HTMLElement && event.currentTarget.contains(focused)) {
            focused.blur();
          }
        }}
      >
        {/* Folds away while the composer has focus, so writing gets the whole
          width and the row goes back to sharing it the moment focus leaves.
          Driven by `:has()` rather than by focus state passed down from the
          composer: the condition is "a field in this row is focused", which is
          something the row can see for itself, and it stays true for whatever
          control ends up sitting here. `max-width` rather than `width` so the
          collapse animates without anyone having to know how wide the control
          is.

          The space between the two is this wrapper's own margin, and it goes
          to zero with it. The row's `gap` survives a collapsed child, and so
          does padding — `max-width: 0` cannot compress padding below its own
          value, so 8px stayed either way and left the full-width composer
          sitting one step inside the thread cards it lines up against.

          Clipped only while it is collapsing. The clip is what makes the fold
          read as the control sliding out of the row rather than shrinking in
          place, but at rest it sat exactly on the button's box and cut the drop
          shadow flush on all four sides. Padding cannot buy that room back, for
          the same reason: `max-width: 0` will not compress padding, so the
          collapsed state would keep it and never reach zero. Clipping only on
          the way out leaves the resting shadow free and the animation
          unchanged. */}
        <div className="pointer-events-auto max-w-40 shrink-0 mr-2 opacity-100 transition-[max-width,margin,opacity] duration-200 group-has-[textarea:focus]/shim:overflow-hidden group-has-[textarea:focus]/shim:mr-0 group-has-[textarea:focus]/shim:max-w-0 group-has-[textarea:focus]/shim:opacity-0">
          <MobileNavMenu active={mobileDestination} onSelect={handleSelectMobileDestination} />
        </div>
        {/* Takes whatever the switcher leaves. `min-w-0` because a flex item
          defaults to `min-width: auto` and refuses to shrink below its content,
          which would push the switcher off the screen instead; `mx-0` drops the
          composer's own mobile side margins, which are for a composer that owns
          the full width and here would just eat into it. */}
        {showsBottomComposer && (
          <Omnibox
            className="mx-0 min-w-0 flex-1"
            newThreadOnly
            onThreadCreated={handleCanvasThreadCreated}
            focusType="never"
          />
        )}
      </div>
    </div>
  );

  // Which panels are showing. Desktop always shows the canvas (the sidebar is
  // the collapsible companion); mobile is one destination at a time, and this is
  // what decides who carries the account switcher.
  const calendarVisible = !isMobile || mobileDestination !== "threads";
  // Whether a calendar is mounted and visible right now, which is what decides
  // if a `/calendar?date=…` link has something on screen to move. The canvas
  // column is a different question: it holds cards here, never the base
  // calendar, so a thread with its column open still has no calendar to focus.
  const isCalendarOnScreen =
    canvasView.kind === "pivot" && canvasView.pivot === "calendar" && calendarVisible;
  // Revealing the calendar so a focused link can show what it just moved is not
  // the user choosing Calendar. Persisting it would overwrite an explicit Chats
  // preference and the next launch would open the wrong tab, so this reveals
  // without remembering.
  const revealCalendar = useCallback(() => {
    if (!isCalendarOnScreen) selectPivot("calendar");
  }, [isCalendarOnScreen, selectPivot]);
  // Mobile only: desktop's account chip lives in the sidebar footer, which a
  // phone has no room for, so its canvas header carries it instead. A phone's
  // header has room for one thing, and the account is what belongs in that
  // corner; settings is reached from the nav menu beside the composer.
  const mobileAccountSwitcher = isMobile && !isWebView ? <AccountSwitcher /> : undefined;
  // Still gated on the sidebar being open, for the same reason the collapse
  // toggle is: a collapsed column stays mounted at zero width, so an ungated
  // footer would keep an invisible-but-focusable switcher in the DOM.
  const sidebarFooter = !isMobile && isThreadListOpen && !isWebView ? <SidebarFooter /> : undefined;
  const sidebarToggle = !isWebView && !isMobile && (
    <SidebarToggleButton
      open={isThreadListOpen}
      onToggle={() => setThreadListOpen((prev) => !prev)}
    />
  );

  // The sidebar — the one thread list, beside whichever view the canvas shows.
  // The account switcher rides here only when the calendar panel isn't showing
  // (mobile Chats tab) to keep it reachable.
  const threadsPanel = (
    <ThreadListPanel
      onSelect={openThread}
      openThreadId={isMobile ? null : openThreadId}
      activePivot={isMobile ? null : activePivot}
      onSelectPivot={isMobile ? undefined : selectPivot}
      isMobile={isMobile}
      isWebView={isWebView}
      // Mobile only: on the Chats tab there is no canvas header to hold it and
      // no footer yet. Desktop's copy lives in the footer below.
      accountSwitcher={!calendarVisible ? mobileAccountSwitcher : undefined}
      footer={sidebarFooter}
      // Only while the sidebar is actually open. A collapsed column stays
      // mounted at zero width rather than unmounting, so an ungated toggle here
      // would still be in the DOM alongside the canvas's copy — invisible, but
      // reachable by keyboard, and a second control for the one action.
      sidebarToggle={isThreadListOpen ? sidebarToggle : undefined}
    />
  );

  // The page header's leading slot, shared by every pivot so the sidebar's
  // control sits in the same place whichever view is showing. Omitted while the
  // sidebar is open: it is holding the toggle itself then.
  //
  // The canvas has no control here. It carries its own, in the header of the
  // card it opened — where the column is on screen to be collapsed, and where
  // the user's eye already is. One in this header would be a second control for
  // a column that is empty whenever it is closed, since dismissing the last card
  // is what closes it.
  const canvasLeading = !isMobile && !isThreadListOpen ? sidebarToggle : undefined;

  // Calendar (canvas) panel — the shared canvas column with home's editable
  // calendar as its body, plus the range-selection + event-click popovers as
  // grid-scoped overlays. When the sidebar is collapsed it carries the chrome
  // that reopens it (mobile hamburger / desktop sidebar toggle).
  const calendarPanel = (
    // `calendar-create-surface` scopes the bottom gradient shim (calendar.css)
    // to the home calendar — week, day, and month all get it; surfaces without
    // the new-chat bar (thread canvas, pickers, sidebar) stay plain.
    // `min-w-0` lets the panel shrink below its content's intrinsic width —
    // without it the mobile day strip's row of date buttons forces the panel
    // wider than the viewport and the header's trailing controls clip off.
    <div className="calendar-create-surface relative flex min-h-0 min-w-0 flex-1 flex-col">
      <CanvasPanel
        leading={canvasLeading}
        accountSwitcher={mobileAccountSwitcher}
        // Mobile asks the destination, desktop asks the sidebar: nothing on
        // mobile writes `isThreadListOpen`, so asking it there pins the strip —
        // day view's only week-level navigation — off.
        showDayStrip={isMobile ? mobileDestination === "calendar" : !isThreadListOpen}
        calendarEditable
        surface={canvasSurface}
        // No stack: the cards draw in the canvas column beside the page now,
        // and drawing them here as well would put the same card on screen
        // twice, the second copy scrimming the calendar the first was opened
        // beside.
      />
    </div>
  );

  // The canvas column, by pivot. Calendar keeps its own panel — it carries the
  // create surface, the shim, and date/view chrome that none of the others want.
  // The rest share one thin frame, so switching cannot change the column's
  // geometry.
  const canvasPanel = (() => {
    if (canvasView.kind === "thread") {
      return (
        <ThreadCanvasView
          // A different thread is a different conversation, not the same one
          // with a new prop: it owns canvas state and a sidebar that should
          // leave with it. Keyed so switching threads is a fresh mount.
          key={canvasView.threadId}
          threadId={canvasView.threadId}
          onLeave={leaveThreadCanvas}
          parentLabel={canvasView.from ? PIVOT_LABELS[canvasView.from] : undefined}
          leading={canvasLeading}
        />
      );
    }
    switch (canvasView.pivot) {
      case "calendar":
        return calendarPanel;
      case "tasks":
        return (
          <TasksPivotView
            focusTaskId={canvasView.itemId ?? null}
            leading={canvasLeading}
            accountSwitcher={mobileAccountSwitcher}
            onSelectThread={openThreadFromTasks}
          />
        );
      case "contacts":
        // The full contacts list, not the profiled-people subset the standalone
        // /people route shows — a saved `/contacts` link lands here, so
        // filtering it would hide most of them. The nav reaches People through
        // its settings tab; this pivot keeps those links working.
        return (
          <ContactsCanvasView
            leading={canvasLeading}
            accountSwitcher={mobileAccountSwitcher}
            openContactId={canvasView.itemId ?? null}
            onOpenContact={openContact}
          />
        );
      case "newchat":
        return (
          <NewChatCanvasView
            // A new request is a new composer. The seed lands as `defaultValue`,
            // which React reads once at mount, and this pivot is what a bare `/`
            // already shows — so a `?input=` deep link, which seeds a commit
            // later, needs the remount to reach the field at all.
            key={newChatSignal}
            seededInput={seededInput}
            onThreadCreated={handleThreadCreated}
            leading={canvasLeading}
            accountSwitcher={mobileAccountSwitcher}
          />
        );
      case "connectors":
        return (
          <ConnectorsCanvasView leading={canvasLeading} accountSwitcher={mobileAccountSwitcher} />
        );
      case "library":
        return (
          <LibraryCanvasView
            leading={canvasLeading}
            accountSwitcher={mobileAccountSwitcher}
            onSelectThread={openThreadFromLibrary}
          />
        );
      case "routines":
        return (
          <RoutinesCanvasView
            leading={canvasLeading}
            accountSwitcher={mobileAccountSwitcher}
            onSeedNewChat={seedNewChat}
            openRoutineId={canvasView.itemId ?? null}
            onOpenRoutine={openRoutine}
          />
        );
      case "skills":
        return (
          <SkillsCanvasView
            leading={canvasLeading}
            accountSwitcher={mobileAccountSwitcher}
            onSeedNewChat={seedNewChat}
            openSkillName={canvasView.itemId ?? null}
            onOpenSkill={openSkill}
          />
        );
    }
  })();

  // The selected event as mobile's whole column, in the same panel chrome a
  // drilled-into contact or routine gets — Calendar named as the way back, so
  // the trail out reads the same whichever destination you drilled from.
  const mobileEventPanel = selectedEventId ? (
    <CanvasPivotPanel
      // Keyed so moving between events is a fresh panel rather than the same one
      // handed a new id: the body holds an edit draft, and carrying it across
      // two different events is how a change lands on the wrong one.
      key={selectedEventId}
      title="Event"
      parentLabel={PIVOT_LABELS.calendar}
      onBack={clearSelectedEvent}
      backStatsigName="calendar-event-back"
      leading={canvasLeading}
      accountSwitcher={mobileAccountSwitcher}
    >
      <EventDetailBody eventId={selectedEventId} onClose={clearSelectedEvent} />
    </CanvasPivotPanel>
  ) : null;

  // An open thread takes the column even though the tab below still reads
  // Chats: it was opened *from* that list, and it is what the URL names. Tapping
  // Chats leaves the thread, which is what puts the list back.
  //
  // A selected event takes it too, and only here: desktop anchors the event's
  // popover to its chip beside the grid, which mobile has neither the room nor
  // the pointer for. Same selection, same address — a different presentation of
  // it, which is the column's to decide.
  let mobileContent: ReactNode = canvasPanel;
  if (mobileShowsThreadList) {
    mobileContent = threadsPanel;
  } else if (selectedEventId) {
    mobileContent = mobileEventPanel;
  }

  return (
    <CalendarPageProvider value={calendarPageValue}>
      {/* Mobile provides no canvas, so consumers fall back (undefined). */}
      <CanvasActionContext.Provider value={showCanvas ? canvasActions : undefined}>
        <ViewWrapper hideHeaderInWebView={false} rightSidebarOverlayable={!isMobile}>
          {/* Spans the page, so an entity link anywhere on it — the
              conversation, a list row, a card — drills onto this canvas rather
              than navigating away from the screen. */}
          <ThreadNavBoundary onOpenThread={openThreadFromLink}>
            <CanvasNavBoundary>
              <CalendarFocusNavBoundary
                // Claimed on every destination, not only while the calendar is
                // showing. Delegating can't work here: `/calendar` is this same
                // page, so navigating to it re-renders without remounting and
                // the URL's date — read once on mount — is never applied. The
                // focus is written to the shared store instead, and `onFocus`
                // switches to the calendar so it opens on that date.
                active
                onFocus={revealCalendar}
              >
                {/* Two full-height panels split by the divider. Desktop always renders the
            split; closing the aside collapses the left column and hands the full
            surface to the calendar. Mobile is one panel at a time, picked by the
            nav menu on the bottom bar. */}
                <div className="relative flex flex-1 min-h-0 overflow-hidden">
                  {/* Desktop splits the surface between the list and the canvas
                    column; mobile holds the single visible panel, which is the
                    whole screen. Only one of the two ever mounts. */}
                  {!isMobile ? (
                    <CanvasSplitPanelLayout
                      leftOpen={isThreadListOpen}
                      left={threadsPanel}
                      right={
                        // The page beside the canvas column, on a divider of its
                        // own. Nested rather than a three-track grid because
                        // each divider is dragged independently and remembers
                        // its own width. The page has no cap: it is what the
                        // user is reading, so it takes the display. The canvas
                        // always reaches its old ceiling and, on a display
                        // with width to spare, grows past it until the page is
                        // down to its design width.
                        <SplitPanelLayout
                          storageKey={CANVAS_COLUMN_SPLIT_STORAGE_KEY}
                          defaultWidthClassName={CANVAS_COLUMN_DEFAULT_WIDTH}
                          minRight={CANVAS_COLUMN_MIN_WIDTH}
                          maxRight={CANVAS_COLUMN_REACH}
                          keepLeft={PAGE_COLUMN_MIN_WIDTH}
                          rightOpen={isCanvasOpen}
                          // The page column never closes, and this split is
                          // nested: the container `100cqw` measures is the
                          // screen's content area, not the half of it this
                          // split gets. Pinned, the page's content box comes
                          // out a thread list too wide — everything centered
                          // in it sits right of center, with its right edge
                          // clipped by the divider.
                          pinLeftWidth={false}
                          left={
                            // A row: the page, then the sidebar it opens into.
                            // The sidebar's spacer is what pushes content aside,
                            // so putting it here means the page column is the
                            // only thing that gives ground — at the page level
                            // it shrank the thread list too, and re-resolved
                            // every container query in the page for the length
                            // of the animation.
                            <div className="flex min-h-0 min-w-0 flex-1 flex-row">
                              <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                                {canvasPanel}
                                {canvasComposer}
                              </div>
                              <RightSidebarHost />
                            </div>
                          }
                          right={
                            // The canvas: a blank column that things are
                            // presented onto. `CanvasLayers` rather than
                            // `CanvasHost` because there is deliberately nothing
                            // underneath — `CanvasHost` mounts a calendar as its
                            // base, and the calendar is a page in the middle
                            // column, so a second copy here would be the same
                            // surface twice on one screen. No background of its
                            // own, exactly like the panels either side: they all
                            // inherit the app's, which is what makes an empty
                            // canvas read as the same ground continuing rather
                            // than a panel that failed to load.
                            <SplitPanelFrame header={null}>
                              <CanvasLayers
                                canvasStack={canvasStack}
                                isCanvasOpen={isCanvasOpen}
                                isFullscreen={isFullscreen}
                                // The card is the column here, so its close
                                // control is the column's — the toggle this
                                // page's header no longer carries.
                                dismiss="collapse"
                              />
                            </SplitPanelFrame>
                          }
                        />
                      }
                    />
                  ) : (
                    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                      {mobileContent}
                    </div>
                  )}
                  {mobileBottomBar}
                </div>
              </CalendarFocusNavBoundary>
            </CanvasNavBoundary>
          </ThreadNavBoundary>
        </ViewWrapper>
      </CanvasActionContext.Provider>
    </CalendarPageProvider>
  );
};

/**
 * Default export wraps with `<Nux>` so `?nux=true` on the landing page
 * (now Calendar, since `/` routes here instead of the legacy threads
 * home) fires the New User Experience tour. The named export above is
 * unwrapped for tests / Storybook / anywhere the tour shouldn't
 * intercept.
 */
const CalendarWithNux = () => (
  <Nux>
    <Calendar />
  </Nux>
);

export default CalendarWithNux;
